import React from "react";
import styles from "./Facts.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import { StaggerAnimation } from "@/components/Animation";
import { Modul_Fakten_Grid } from "../../wp/types/acf-module-types";
import { FactsGridOptionsType } from "@/types/ModuleOptionsType";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Section } from "@/components/Section/Section";
import { Headline } from "@/components/Headline/Headline";
import { RunningDigit } from "./RunningDigit";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { removeExtraSpacesFromClassNames as removeWhitespace } from "@/utils/removeExtraSpacesFromClassNames";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

export type FactsModuleType = Modul_Fakten_Grid;

export type FactsElementType = {
  title: string;
  content: string;
};

interface FactsProps {
  data: FactsModuleType;
  factsGridOptions: FactsGridOptionsType;
}

const Facts = ({ data, factsGridOptions }: FactsProps) => {
  const { getLabel } = useGlobalContext();

  const {
    section_padding_class,
    section_margin_class,
    background_color_class,
    text_align_class,
    id,
    grid_columns,
  } = data.layout;

  const { headline, headline_html_tag, elements, number_type } =
    data.facts_grid;
  const show_line = factsGridOptions?.show_line;
  const { show_border_radius, show_box_shadow, show_card_box } =
    factsGridOptions?.box || {
      show_border_radius: false,
      show_box_shadow: false,
      show_card_box: false,
    };
  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  return (
    <Section
      id={id}
      className={removeWhitespace(
        `facts ${background_color_class} ${section_padding_class} ${section_margin_class}`,
      )}
      disableColumns
      role="region"
      aria-label={getLabel("facts_aria_label", "Key facts and statistics")}
    >
      <div className="columns is-multiline is-centered">
        {headline && (
          <div className="column is-12">
            <Headline
              headline={headline}
              headline_html_tag={headline_html_tag}
              extraClass={text_align_class}
              disableAnimation
            />
          </div>
        )}

        <div className="column is-12">
          <StaggerAnimation
            animation="slide-up-no-opacity"
            staggerDelay={50}
            className={removeWhitespace(
              `columns is-multiline is-centered ${styles["facts-columns"]}  ${show_line ? styles["show-line"] : ""} ${show_line && grid_columns === "four-cols" ? styles["four-cols"] : ""}`,
            )}
          >
            {Array.isArray(elements) &&
              elements.map((element, key) => {
                const cleanTitile = customSanitize(element.title).replace(
                  /<[^>]*>?/gm,
                  "",
                );

                return (
                  <article
                    role="listitem"
                    data-fontsize={number_type}
                    key={key}
                    className={removeWhitespace(
                      `column ${grid_columns === "three-cols" ? "is-4-tablet" : "is-6-tablet is-3-widescreen"}`,
                    )}
                  >
                    <div
                      className={removeWhitespace(
                        `${styles["facts-element"]} ${show_card_box && !show_line ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_box_shadow && show_card_box ? "has-box-shadow" : ""}`,
                      )}
                    >
                      {element.title && (
                        <div
                          className={`${number_type === "big-number" ? "facts-big" : "facts-small"} is-clipped`}
                          aria-label={`${cleanTitile}${element.unit ? ` ${element.unit}` : ""}`}
                        >
                          <RunningDigit ariaHidden value={cleanTitile} />

                          {element.unit && (
                            <span
                              aria-hidden
                              className={removeWhitespace(
                                `${styles["unit"]} has-text-left`,
                              )}
                            >
                              {element.unit}
                            </span>
                          )}
                        </div>
                      )}

                      {element.content && (
                        <ContentTag
                          content={element.content}
                          extraClass={`${styles["content"]} ${text_align_class === "has-text-left" ? "is-align-self-flex-start" : ""} ${text_align_class === "has-text-centered" ? "has-text-centered" : ""}`}
                        />
                      )}
                    </div>
                  </article>
                );
              })}
          </StaggerAnimation>
        </div>
      </div>
    </Section>
  );
};

export default Facts;
